import { Colors, FOCUS_RING, Icon, Text } from '@amelio/ui-library';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type {
    GeneratedAgendaAlert,
    GeneratedAgendaAlertType,
} from '@/components/MeetingAgendaBuilder/agendaViewModel';
import type { TranslationKey } from '@/i18n-key';

const ALERT_LABEL_KEYS: Record<GeneratedAgendaAlertType, TranslationKey> = {
    meetingFit: 'meetingAgenda.aiAgenda.review.alertType.meetingFit',
    tension: 'meetingAgenda.aiAgenda.review.alertType.tension',
    boundary: 'meetingAgenda.aiAgenda.review.alertType.boundary',
};

/** Each alert type in its own warning tone: another meeting fits, a question works against the goal, a line the plan does not cross. */
const ALERT_STYLES: Record<GeneratedAgendaAlertType, { box: string; title: string; text: string }> =
    {
        meetingFit: {
            box: 'border-amber-300 bg-amber-50',
            title: 'text-amber-800',
            text: 'text-amber-700',
        },
        tension: {
            box: 'border-orange-300 bg-orange-50',
            title: 'text-orange-800',
            text: 'text-orange-700',
        },
        boundary: {
            box: 'border-rose-300 bg-rose-50',
            title: 'text-rose-800',
            text: 'text-rose-700',
        },
    };

interface GeneratedAgendaReviewProps {
    /** The heading over the summary. */
    title: string;
    summary: string;
    alerts: GeneratedAgendaAlert[];
    /** When set, a close button lets the manager dismiss the review. */
    onDismiss?: () => void;
    dataTestId?: string;
}

/**
 * What the AI wrote for the manager with a generated plan: the summary of what it changed and why, then its alerts,
 * each in the warning tone of its type. Nothing is shown for a plan that comes with neither (the post-review plan).
 */
export const GeneratedAgendaReview = ({
    title,
    summary,
    alerts,
    onDismiss,
    dataTestId = 'generated-agenda-review',
}: GeneratedAgendaReviewProps) => {
    const { t } = useTranslation();
    const titleId = useId();

    if (!summary && alerts.length === 0) {
        return null;
    }

    return (
        <section
            aria-labelledby={titleId}
            className={
                'relative flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4'
            }
            data-testid={dataTestId}
        >
            <div className={'flex items-start justify-between gap-3'}>
                <Text
                    id={titleId}
                    weight={'semibold'}
                    className={'text-gray-800'}
                >
                    {title}
                </Text>

                {onDismiss && (
                    <button
                        type={'button'}
                        onClick={onDismiss}
                        aria-label={t('meetingAgenda.aiAgenda.review.dismiss')}
                        data-testid={`${dataTestId}-dismiss`}
                        className={`shrink-0 rounded-full p-1 hover:bg-slate-200 ${FOCUS_RING}`}
                    >
                        <Icon
                            name={'essential-close'}
                            size={'sm'}
                            color={Colors.BASE_500}
                            ariaHidden
                        />
                    </button>
                )}
            </div>

            {summary && (
                <p
                    className={'text-sm text-gray-600'}
                    data-testid={`${dataTestId}-summary`}
                >
                    {summary}
                </p>
            )}

            {alerts.length > 0 && (
                <ul
                    aria-label={t('meetingAgenda.aiAgenda.review.alertsLabel')}
                    className={'flex flex-col gap-2'}
                >
                    {alerts.map((alert, index) => {
                        const style = ALERT_STYLES[alert.type];
                        return (
                            <li
                                key={`${alert.type}-${index}`}
                                className={`flex items-start gap-2 rounded-lg border p-3 ${style.box}`}
                                data-testid={`${dataTestId}-alert-${alert.type}`}
                            >
                                <span
                                    aria-hidden={'true'}
                                    className={'shrink-0'}
                                >
                                    ⚠️
                                </span>
                                <div className={'flex flex-col gap-1'}>
                                    <p className={`text-sm font-semibold ${style.title}`}>
                                        {t(ALERT_LABEL_KEYS[alert.type])}
                                    </p>
                                    <p className={`text-sm ${style.text}`}>{alert.text}</p>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
};
